/**
 * **一张字卡**（字网格里的一格）—— 「浏览与编辑」与「按书看字表」**共用同一份**。
 *
 * ## 为什么抽成一个组件（2026-10-18 用户对两处的要求）
 *
 * 用户先要求把「浏览与编辑」的字列表换成「录入汉字」那种网格：
 *
 * > "一边是以椭圆框显示，**一行显示一个字**。另一边是圆角框，显示得比较好，
 * >  **一行显示几个字**。所以，**录入汉字这个模块，显示汉字的方式比较好**……
 * >  把这一刻显示汉字的方法，**替换掉浏览查询模块里显示汉字的方式**……"
 *
 * 紧接着又说**家长端那块字卡不该删**（删的只是管理员「按书录入」里的那块）：
 *
 * > "我不是说家长端那块字卡要删除，我说的是管理员端录入字词的那一块不需要再
 * >  显示字词了，因为你已经有浏览那一块了吧？"
 *
 * 于是两处都要有网格。**如果各写一份**，"改一处忘一处"只是时间问题
 * （本项目已经栽过好几次，例如计时面板与"多音字"口径都是两份实现漂移）。
 * 所以卡片与列数只在这里定义一次：
 *
 *   · `HANZI_GRID_CLASS` —— 手机 3 列 / 小屏 6 列 / 桌面 8 列；
 *   · `HanziCard` —— 拼音在上、字在下；**只有已确认标注**的多音字上浅琥珀底色；
 *     框里**零按钮**，点整张卡 = 打开浮窗（`HanziReadingsModal`）。
 *
 * ## 底色 = 这张卡当前处于哪种状态（2026-10-20 用户原话）
 *
 * > "录入汉字这里，有些多音字要标音。一开始没标音的时候，它是**玫瑰色的底色**。
 * >  你再改一下：**就当我标音之后呢，把它底色变成另外一种颜色**，表示它**已经标音了**。
 * >  这样我看的时候就不会再去注意它了……"
 *
 * 所以预览里的底色有**三种**，一眼可分（详见下面 `className` 里的注释）：
 *
 * | 状态 | 类名 | 颜色 |
 * | --- | --- | --- |
 * | 重复出现 + **还没标音**（要你处理） | `border-rose-300 bg-rose-50` | 浅玫瑰 |
 * | 重复出现 + **已经标音**（不用再管） | `border-emerald-300 bg-emerald-50` | 浅绿 |
 * | 文件里标了「?」/ 和字典不一致 + **还没确认**（点它确认） | `border-orange-300 bg-orange-50` | 浅橙 |
 * | 上面那种 + **已经确认**（不用再管） | `border-emerald-300 bg-emerald-50` | 浅绿 |
 * | 只出现一次 / 浏览页的普通字 | `border-slate-200 bg-white` | 白 |
 * | 浏览页里**已确认标注的多音字** | `border-amber-300 bg-amber-50` | 浅琥珀（原样） |
 *
 * ## ⚠️ 2026-10-20（第七轮）：「不确定 `?`」两张新状态
 *
 * 用户原话：
 *
 * > "你识别时，**对你不确定的读音做个标识**……**系统识别这个标识**，在预览里
 * >  **用底色标出这些字**；我点那个字，弹窗里看那个音对不对……点完确认之后，
 * >  **底色要改变**。"
 *
 *   · `uncertain`（带 `?`、还没确认）⇒ **浅橙**（`orange-50`）：与"多音字的浅琥珀
 *     `amber-50`"**刻意分开**（橙更红、饱和一点），也与玫瑰/浅绿分开；
 *   · `confirmed`（带 `?`、确认过了）⇒ **浅绿**（与"重复+已标音"同一个绿：
 *     对用户来说它们是同一件事 —— "这个音我看过了"）。
 *   · ⚠️ 优先级：**不确定 > 已确认 > 重复**（文件标了 `?` 就一定是橙，
 *     哪怕它已经写了读音、也哪怕它是重复字；用户确认之后才变绿）。
 *
 * ⚠️ 跳转高亮（品牌蓝 `brand-100` 脉冲）是**叠在**底色上的短暂动画，2 秒后自动消失：
 *    动画只写 `50%` 那一帧，0% / 100% 仍旧用卡片自己的底色 ⇒ 两者同时出现不打架。
 *
 * ⚠️ 两处的**权限差别不在卡片上**：同一张卡在家长端与管理员端长得一模一样，
 *    区别只在点开之后浮窗里渲不渲染写入口（见 `HanziReadingsModal` 的 `canEdit`）。
 *    ⛔ 不要为了"家长只读"给卡片加 `disabled` / 灰底 —— 那会变成一个点不动的框，
 *    而用户明确要求"恢复后家长端那块不能是点了没反应的按钮"。
 */
import { cn } from '../../lib/utils';
import type { HanziView } from '../../lib/api/dictation';

/**
 * 字网格的列数（**两处共用的唯一一份**）。
 *
 * 手机 3 列 → 小屏 6 列 → 桌面 8 列：与「按书录入」原来那张网格逐字一致
 * （用户原话"录入汉字这个模块，显示汉字的方式比较好"指的就是它）。
 */
export const HANZI_GRID_CLASS = 'grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-8';

export interface HanziCardProps {
  item: HanziView;
  /** 点整张卡：打开「读音与造词」浮窗。**两种模式都传**（家长也要能看）。 */
  onOpen: () => void;
  /**
   * 卡片上显不显示拼音。
   *
   * 默认 `true`：浏览与编辑 / 按书看字表**一个字都不改**。
   *
   * ⚠️ 2026-10-20 用户要求**导入预览也要显示拼音**（"按照你们库里面的音，直接给它
   *    标一个拼音，我会逐字去核对他的拼音对不对"）—— 预览侧现在也传 `true`
   *    （见 `ImportListingPreview`）。更早那句"拼音你先不标在导入结果那里"**以本次为准**。
   *    显示的是 `listingReading ?? pinyin`：标过音的位置显示**这个位置标定的音**，
   *    没标过的显示**字的默认读音**；空读音显示「（没有读音）」。
   */
  showPinyin?: boolean;
  /**
   * **重复出现的字**（导入预览，2026-10-19）：用**浅玫瑰底色**标出来。
   *
   * ⚠️ 与"多音字的琥珀色"刻意区分开：重复是"这个位置可能要你标音"，
   *    多音字是"这个字你确认过有多个读音"。
   * ⚠️ 与 `marked` 同时成立时底色换成**浅绿**（标过音了，不必再注意它）。
   */
  duplicate?: boolean;
  /**
   * **这个位置已经标了音**（导入预览，2026-10-19；2026-10-20 起兼作底色判据）。
   *
   * ⚠️ 与 `duplicate` 同时成立 ⇒ 底色从浅玫瑰换成**浅绿**（`bg-emerald-50`）：
   *    用户原话"就当我标音之后呢，把它底色变成另外一种颜色，表示它已经标音了。
   *    这样我看的时候就不会再去注意它了"。
   * ⚠️ 只出现一次的字即使标了音也保持白底（用户点名"没重复的字 → 保持普通白底"）。
   * ⚠️ 与 `showPinyin` 无关：浏览页不传它，行为一个字都不变。
   */
  marked?: boolean;
  /**
   * **读音要用户确认、还没确认**：**浅橙底色**。
   *
   * 两种来源（2026-10-21 起）：
   *   · 文件里标了「?」（生成文件的人没把握）；
   *   · **文件里写的拼音 ≠ 字典拼音**（单音字按字典落库、多音字按文件落库，
   *     但两种都要用户对着课本核一遍）。
   *
   * 只由导入预览传（`position.uncertain`）。点这张卡 → 浮窗里「✅ 确认就是「X」」
   * 或换一个读音 ⇒ 重新预览后 `uncertain=false`、`confirmed=true` ⇒ 变浅绿。
   * ⚠️ 优先级最高：`uncertain` 时即使 `duplicate` / `marked` 也画橙（用户要求
   *    "和字典不一致的依然要用底色呈现"）。
   */
  uncertain?: boolean;
  /**
   * **上面那种待确认、用户已经确认**：**浅绿底色**。
   *
   * 用途是"点完确认之后底色要改变"：一个只出现一次的字确认完若按老规则会退回
   * 白底，用户就看不出"我确认过了"。所以这一态独立于 `duplicate`。
   */
  confirmed?: boolean;
  /** 卡片底部那行小字（预览里放位置，例如 `第 3 行第 2 个字`）。 */
  caption?: string;
  /**
   * 把这张卡的 DOM 交给「同一个字互相跳转」的登记表（`useHanziJump().register(key)`）。
   *
   * ⚠️ 只由导入预览与「浏览与编辑」传；「按书看字表」那张网格不传 ⇒ 行为一个字不变。
   */
  cardRef?: ((element: HTMLButtonElement | null) => void) | undefined;
  /**
   * **刚刚跳到这张卡**：品牌蓝浅底**柔和脉冲** 2 秒（2~3 次），到点由
   * `useHanziJump` 自动清掉，底色干净移除。
   *
   * ⚠️ 高亮是**受控**的（`jump.activeKey === key`），这里只负责画；
   *    ⛔ 不要在这里自己起定时器 —— 否则两处各有一套，迟早不一致。
   */
  highlighted?: boolean;
}

export function HanziCard({
  item,
  onOpen,
  showPinyin = true,
  duplicate = false,
  marked = false,
  uncertain = false,
  confirmed = false,
  caption,
  cardRef,
  highlighted = false,
}: HanziCardProps) {
  const polyphonic = item.polyphonic;
  /**
   * **这一行自己的读音**（迁移 0018）：位置标了音就用它，没标就用字的默认读音。
   * ⚠️ 不改 `item.pinyin` 本身（它是"字的默认读音"，编辑表单会用）。
   */
  const shownPinyin = item.listingReading ?? item.pinyin;
  return (
    <li className="flex min-w-0">
      <button
        ref={cardRef}
        type="button"
        onClick={onOpen}
        /** 给测试与样式一个稳定钩子：**只有已标注**的字才是 `true`。 */
        data-polyphonic={polyphonic ? 'true' : 'false'}
        data-duplicate={duplicate ? 'true' : 'false'}
        /** 这个位置**已经标了音**（底色也因此从玫瑰换成浅绿）。 */
        data-marked={marked ? 'true' : 'false'}
        /** 读音要确认、**还没确认**（黄卡）。 */
        data-uncertain={uncertain ? 'true' : 'false'}
        /** 待确认过、**已经确认**（绿卡）。 */
        data-confirmed={confirmed ? 'true' : 'false'}
        /** 跳转刚落地的那张卡（2 秒后自动变回 `false`，不是永久标记）。 */
        data-jump-highlight={highlighted ? 'true' : 'false'}
        aria-label={
          showPinyin
            ? `${item.charText}（${shownPinyin === '' ? '没有读音' : shownPinyin}）${
                polyphonic ? '，多音字' : ''
              }${duplicate ? '，重复出现' : ''}${uncertain ? '，读音待确认' : ''}${
                confirmed ? '，已确认' : ''
              }`
            : `${item.charText}${duplicate ? '，重复出现' : ''}${marked ? '，已标音' : ''}${
                uncertain ? '，读音待确认' : ''
              }${confirmed ? '，已确认' : ''}`
        }
        title={
          uncertain
            ? `${item.charText}${
                shownPinyin === '' ? '' : `（${shownPinyin}）`
              } 的读音还要你确认（文件里标了「?」，或和字典不一样）—— 点它确认这个音`
            : confirmed
              ? `${item.charText}${
                  shownPinyin === '' ? '' : `（${shownPinyin}）`
                } —— 这个音你已经确认过了`
              : duplicate
                ? `${item.charText} 重复出现了 —— 点它给这个位置选读音`
                : showPinyin
                  ? polyphonic
                    ? `多音字：${shownPinyin === '' ? '读音待填' : shownPinyin} —— 点开看每个读音的词`
                    : `点开看读音与造词（${shownPinyin === '' ? '没有读音' : shownPinyin}）`
                  : `${item.charText} —— 点它选读音`
        }
        className={cn(
          'flex w-full min-w-0 flex-col items-center justify-center rounded-xl border px-2 py-1.5 text-center',
          /** 手机可点性：整张卡至少 44px 高（导入预览里点它开浮窗）。 */
          'min-h-[44px]',
          'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-200',
          /**
           * ⚠️ **刚跳到这张卡**（2026-10-20 用户要求：从 ring 改成底色闪烁）：
           *    · 品牌蓝浅底（`brand-100` #d9eaff）柔和脉冲 **2.5 次 / 共 2 秒**
           *      （keyframes / 时长在 `tailwind.config.js` 的 `jump-flash`）；
           *    · 到点 `useHanziJump` 把 `highlighted` 置回 false ⇒ 底色干净移除，
           *      动画本身只跑一轮、不循环；
           *    · ⛔ **不用 ring**：用户明确说"以底色的形式闪烁"，而且边框/尺寸类会让
           *      卡片"跳一格"；`background-color` 不参与布局，绝不引起尺寸/位置变化；
           *    · ♿ `prefers-reduced-motion: reduce` ⇒ 不闪，常亮 `brand-100`
           *      2 秒后随 `highlighted` 一起消失。
           */
          highlighted && 'animate-jump-flash motion-reduce:animate-none motion-reduce:bg-brand-100',
          /**
           * ⚠️ 2026-10-19：预览里"重复出现的字"用**浅玫瑰**；浏览页的琥珀色
           *    （已标注多音字）**原样保留**。
           *
           * ⚠️ 2026-10-20（用户原话）：**标了音就换一种底色** —— "就当我标音之后呢，
           *    把它底色变成另外一种颜色，表示它**已经标音了**……我看的时候就不会再去
           *    注意它了"。
           *
           * ⚠️ 2026-10-20 第七轮：文件里标了「?」的位置**先黄后绿** ——
           *    `uncertain` → **浅橙**（`orange-50`）、`confirmed` → **浅绿**。
           *    优先级：**不确定 > 已确认 > 重复**（用户原话："文件标了 `?` → 即使
           *    有读音，也必须是黄，直到用户确认"）。
           *
           * 五种状态、五种底（预览里只会出现前四种）：
           *
           *    · `uncertain`          → **浅橙**（`orange-50` + `orange-300` 边框）：
           *      文件里标了「?」，还要你点它确认（⭐ 本轮新增）；
           *    · `confirmed`          → **浅绿**（`emerald-50`）：确认过了，不用再管
           *      （⭐ 本轮新增；只出现一次的字确认完也变绿）；
           *    · `duplicate && marked` → **浅绿**：这个位置已经标过音；
           *    · `duplicate && !marked` → **浅玫瑰**（`rose-50`）：还要你点它选读音；
           *    · `polyphonic`（!duplicate） → **浅琥珀**（`amber-50`，浏览页原样）：
           *      这个字你确认过多读音；
           *    · 其余 → **白**（只出现一次、也没标音）。
           *
           * ⚠️ 浅橙刻意避开琥珀（`amber` = 多音字）：`orange` 更红、饱和一点，
           *    两个并排时看得出是两种意思（"要你确认" vs "这是个多音字"）。
           * ⚠️ 浅绿刻意避开玫瑰（粉）、琥珀（黄）、品牌蓝（`brand-100` 脉冲）三种色系：
           *    与跳转高亮同时出现时，蓝脉冲在绿底上仍然看得见（动画只覆盖 50% 那一帧）。
           */
          uncertain
            ? 'border-orange-300 bg-orange-50'
            : confirmed
              ? 'border-emerald-300 bg-emerald-50'
              : duplicate
                ? marked
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-rose-300 bg-rose-50'
                : polyphonic
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-slate-200 bg-white',
        )}
      >
        {/*
          拼音在上（用户要**逐字核对**，所以字号从 11px 提到 12px、不再截断）：
          标过音的位置显示**这个位置标定的音**（`listingReading`），没标过的显示
          **字的默认读音**（`pinyin`）；空读音显示「（没有读音）」。
          ⚠️ 用 `min-h-4` 而不是固定 `h-4`：极少数长读音宁可让卡片高一点，
             也不要被 `truncate` 截掉半个拼音（那会让"核对"失去意义）。
        */}
        {showPinyin && (
          <span className="block min-h-4 w-full break-words font-mono text-xs leading-4 text-slate-600">
            {shownPinyin === '' ? '（没有读音）' : shownPinyin}
          </span>
        )}
        {/* 字在下（字号不变：视觉主体仍是这个字） */}
        <span className="block w-full truncate text-lg font-semibold leading-7 text-slate-900">
          {item.charText}
        </span>
        {polyphonic && (
          <span className="mt-0.5 block text-[10px] font-medium text-amber-700">多音字</span>
        )}
        {duplicate && (
          <span className="mt-0.5 block text-[10px] font-medium text-rose-700">重复</span>
        )}
        {/*
          ⚠️ 「已标音」小字**保留**（用户倾向双保险）：底色换成浅绿之外，再用三个字
             明说一遍 —— 色弱 / 夜里看屏幕时底色未必分得清，文字是不丢的那一层。
             拼音现在也显示了（`showPinyin`），所以这行不再承担"替代拼音"的职责。
          ⚠️ 2026-10-20 第七轮：带 `?` 的位置先说「待确认」，确认之后说「已确认」——
             这两种状态各**取代**「已标音」那三个字（否则一张卡上会并列
             「已标音」和「待确认」，自相矛盾）。
        */}
        {uncertain ? (
          <span className="mt-0.5 block text-[10px] font-medium text-orange-700">待确认</span>
        ) : confirmed ? (
          <span className="mt-0.5 block text-[10px] font-medium text-emerald-700">已确认</span>
        ) : (
          marked && (
            <span className="mt-0.5 block text-[10px] font-medium text-emerald-700">已标音</span>
          )
        )}
        {caption !== undefined && caption !== '' && (
          <span className="mt-0.5 block w-full truncate text-[10px] text-slate-500">{caption}</span>
        )}
      </button>
    </li>
  );
}
